import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Radio,
  Button,
  Table,
  Tag,
  Badge,
  Space,
  message,
  Spin,
  Empty,
  Popconfirm,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  TeamOutlined,
  MailOutlined,
  BookOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { exportApi } from '../../services/export';
import type { DataExport as DataExportRecord, CreateExportRequest } from '../../types/lifecycle';
import {
  ExportTypeNames,
  ExportFormatNames,
  ExportStatusNames,
  formatFileSize,
  getExportTypeColor,
} from '../../types/lifecycle';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;

// 导出类型选项
const exportTypeOptions = [
  { label: '提案数据', value: 'proposals', icon: <FileTextOutlined /> },
  { label: '红人数据', value: 'influencers', icon: <TeamOutlined /> },
  { label: '邀约数据', value: 'campaigns', icon: <MailOutlined /> },
  { label: '知识库数据', value: 'knowledge', icon: <BookOutlined /> },
  { label: '全部数据', value: 'all', icon: <DatabaseOutlined /> },
];

// 导出格式选项
const formatOptions = [
  { label: 'CSV', value: 'csv' },
  { label: 'Excel', value: 'excel' },
  { label: 'JSON', value: 'json' },
];

export const DataExport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exportType, setExportType] = useState<CreateExportRequest['export_type']>('proposals');
  const [format, setFormat] = useState<CreateExportRequest['format']>('csv');
  const [exports, setExports] = useState<DataExportRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 获取导出列表
  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await exportApi.getList(page, pageSize);
      if (res.data) {
        setExports(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch exports:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  // 创建导出任务
  const handleCreateExport = async () => {
    setCreating(true);
    try {
      const res = await exportApi.create(exportType, format);
      if (res.data) {
        message.success('导出任务已创建，请稍后刷新查看状态');
        fetchExports();
      }
    } catch (error) {
      console.error('Failed to create export:', error);
    } finally {
      setCreating(false);
    }
  };

  // 下载导出文件
  const handleDownload = async (record: DataExportRecord) => {
    if (record.status !== 'completed') {
      message.warning('文件尚未准备好，请稍后再试');
      return;
    }

    try {
      const blob = await exportApi.download(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `export_${record.export_type}_${record.id}.${record.format}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('下载成功');
    } catch (error) {
      console.error('Failed to download export:', error);
      message.error('下载失败，请稍后重试');
    }
  };

  // 获取状态 Badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { status: 'processing' | 'success' | 'error' | 'default'; text: string }> = {
      pending: { status: 'processing', text: '等待中' },
      processing: { status: 'processing', text: '处理中' },
      completed: { status: 'success', text: '已完成' },
      failed: { status: 'error', text: '失败' },
      expired: { status: 'default', text: '已过期' },
    };
    const config = statusConfig[status] || { status: 'default', text: status };
    return <Badge status={config.status} text={config.text} />;
  };

  // 计算过期剩余天数
  const getExpiresDays = (expiresAt: string) => {
    if (!expiresAt) return '-';
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return '已过期';
    return `${diffDays} 天`;
  };

  // 表格列定义
  const columns: ColumnsType<DataExportRecord> = [
    {
      title: '数据类型',
      dataIndex: 'export_type',
      key: 'export_type',
      render: (type: string) => (
        <Tag color={getExportTypeColor(type)}>{ExportTypeNames[type] || type}</Tag>
      ),
    },
    {
      title: '格式',
      dataIndex: 'format',
      key: 'format',
      render: (f: string) => ExportFormatNames[f] || f,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size: number) => (size > 0 ? formatFileSize(size) : '-'),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (expiresAt: string, record) =>
        record.status === 'completed' ? getExpiresDays(expiresAt) : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => (time ? new Date(time).toLocaleString() : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: DataExportRecord) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          disabled={record.status !== 'completed'}
          onClick={() => handleDownload(record)}
        >
          下载
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: 8 }}>
          数据导出
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          导出您的业务数据，支持多种格式。导出文件将在 7 天后自动过期删除。
        </Paragraph>

        {/* 创建导出卡片 */}
        <Card title="创建导出任务" style={{ marginBottom: 24 }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                数据范围
              </Text>
              <Radio.Group
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                {exportTypeOptions.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    <Space>
                      {opt.icon}
                      {opt.label}
                    </Space>
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                导出格式
              </Text>
              <Radio.Group
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                {formatOptions.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={creating}
              onClick={handleCreateExport}
            >
              创建导出任务
            </Button>
          </Space>
        </Card>

        {/* 导出记录列表 */}
        <Card title="导出记录">
          <Table
            columns={columns}
            dataSource={exports}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            locale={{ emptyText: <Empty description="暂无导出记录" /> }}
          />
        </Card>
      </div>
    </Spin>
  );
};

export default DataExport;
